import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User, Briefcase, CheckCircle2, AlertCircle, TrendingUp,
  ArrowRight, Edit3, Globe, Star, Loader2, MapPin, Bell,
  DollarSign, ChevronRight, X, ExternalLink, FileText, Percent,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABILITY_STYLES = {
  available: { label: 'Müsait', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  busy: { label: 'Meşgul', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  unavailable: { label: 'Müsait Değil', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const OFFER_STATUS = {
  pending:  { label: 'Bekliyor', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  accepted: { label: 'Kabul Edildi', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  declined: { label: 'Reddedildi', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};

// Commission Modal
function CommissionModal({ profile, onClose }) {
  const { token } = useAuth();
  const [form, setForm] = useState({ job_title: '', job_amount: '', job_currency: profile?.daily_rate_currency || 'USD' });
  const [commRate, setCommRate] = useState(4.0);
  const [loading, setLoading] = useState(false);

  const commission = form.job_amount ? (parseFloat(form.job_amount) * commRate / 100).toFixed(2) : '—';

  const startPayment = async () => {
    if (!form.job_title.trim() || !form.job_amount) { toast.error('Tüm alanları doldurun'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/billing/freelancer-commission`, {
        job_title: form.job_title,
        job_amount: parseFloat(form.job_amount),
        job_currency: form.job_currency,
        commission_rate: commRate,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ödeme başlatılamadı');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold flex items-center gap-2">
            <Percent className="w-4 h-4 text-red-500" />Platform Komisyonu Öde
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">İş / Proje Adı *</label>
            <input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))}
              placeholder="Örn. Dubai Expo Stand Tasarımı"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Toplam Kazanç *</label>
              <input type="number" value={form.job_amount} onChange={e => setForm(f => ({ ...f, job_amount: e.target.value }))}
                placeholder="1000"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Para Birimi</label>
              <select value={form.job_currency} onChange={e => setForm(f => ({ ...f, job_currency: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none">
                {['USD','EUR','TRY','GBP','AED'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Komisyon oranı</span>
              <span className="font-bold text-red-600">%{commRate}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-gray-600">Ödenecek komisyon</span>
              <span className="font-black text-gray-900">{commission} {form.job_currency}</span>
            </div>
          </div>
          <p className="text-xs text-gray-400">Komisyon, platform aracılığıyla bulduğun müşterilere verilen hizmet için geçerlidir.</p>
        </div>
        <Button onClick={startPayment} disabled={loading} className="w-full mt-5 bg-red-600 hover:bg-red-700 text-white gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
          Stripe ile Öde
        </Button>
      </div>
    </div>
  );
}

const ScoreRing = ({ score }) => {
  const r = 32;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? '#16a34a' : score >= 50 ? '#f59e0b' : '#dc2626';
  return (
    <div className="relative w-20 h-20 flex items-center justify-center">
      <svg className="absolute inset-0" width="80" height="80" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#f1f5f9" strokeWidth="8" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 40 40)"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div className="text-center">
        <p className="text-xl font-black text-gray-900 leading-none">{score}</p>
        <p className="text-[9px] text-gray-400 uppercase tracking-wide">puan</p>
      </div>
    </div>
  );
};

export default function FreelancerDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobOffers, setJobOffers] = useState([]);
  const [showCommission, setShowCommission] = useState(false);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/freelancer/dashboard-stats`, { headers }),
      axios.get(`${API}/freelancer/profile`, { headers }),
      axios.get(`${API}/freelancer/job-offers`, { headers }).catch(() => ({ data: { items: [] } })),
    ]).then(([sRes, pRes, offersRes]) => {
      setStats(sRes.data);
      setProfile(pRes.data);
      setJobOffers(offersRes.data?.items || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const respondToOffer = async (offerId, status) => {
    try {
      await axios.put(`${API}/freelancer/job-offers/${offerId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setJobOffers(prev => prev.map(o => o.id === offerId ? { ...o, status } : o));
      toast.success(status === 'accepted' ? 'Teklif kabul edildi!' : 'Teklif reddedildi');
    } catch { toast.error('İşlem başarısız'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-red-500" />
    </div>
  );

  const avStyle = AVAILABILITY_STYLES[stats?.availability || 'available'];
  const missingProfile = stats?.profile_score < 60;
  const pendingOffers = jobOffers.filter(o => o.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900">
            Merhaba, {user?.name?.split(' ')[0] || 'Freelancer'} 👋
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm text-gray-500">{stats?.category_name}</span>
            {profile?.city && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <MapPin className="w-3 h-3" />{profile.city}
              </span>
            )}
            <Badge className={`text-xs border ${avStyle.cls}`}>{avStyle.label}</Badge>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowCommission(true)} variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 gap-2 text-sm">
            <Percent className="w-4 h-4" />Komisyon Öde
          </Button>
          <Button onClick={() => navigate('/app/freelancer/profile')}
            className="bg-red-600 hover:bg-red-700 text-white gap-2">
            <Edit3 className="w-4 h-4" />Profili Düzenle
          </Button>
        </div>
      </div>

      {/* Profile completion alert */}
      {missingProfile && (
        <div data-testid="profile-completion-alert" className="flex items-start gap-4 bg-amber-50 border-2 border-dashed border-amber-300 rounded-2xl px-5 py-4">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-amber-800 text-sm">Profilin %{stats?.profile_score || 0} tamamlandı</p>
            <p className="text-xs text-amber-600 mt-0.5">Profil fotoğrafı, biyografi ve portfolyo ekleyerek görünürlüğünü artır.</p>
          </div>
          <Button size="sm" onClick={() => navigate('/app/freelancer/profile')}
            className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white gap-1 text-xs">
            Tamamla <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="col-span-1 bg-white rounded-2xl border border-gray-100 p-5 flex flex-col items-center justify-center shadow-sm">
          <ScoreRing score={stats?.profile_score || 0} />
          <p className="text-xs font-semibold text-gray-500 mt-2">Profil Skoru</p>
        </div>
        {[
          { icon: Briefcase, label: 'Portfolyo', value: stats?.portfolio_count || 0, unit: 'proje', color: 'bg-blue-50 text-blue-600' },
          { icon: Bell, label: 'Gelen Teklifler', value: pendingOffers.length, unit: 'bekliyor', color: pendingOffers.length > 0 ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-400' },
          { icon: Globe, label: 'Günlük Ücret', value: profile?.daily_rate ? `${profile.daily_rate} ${profile.daily_rate_currency || 'USD'}` : '—', unit: '', color: 'bg-emerald-50 text-emerald-600' },
        ].map(({ icon: Icon, label, value, unit, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-gray-900 leading-tight">{value}</p>
              {unit && <p className="text-xs text-gray-500">{unit}</p>}
              <p className="text-xs text-gray-400">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Job Offers Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between p-5 border-b border-gray-50">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-4 h-4 text-red-500" />Gelen İş Teklifleri
              {pendingOffers.length > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{pendingOffers.length}</span>
              )}
            </h3>
            <Link to="/job-board" className="text-xs text-red-500 hover:text-red-700">İş İlanları →</Link>
          </div>
          <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
            {jobOffers.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Henüz iş teklifi yok</p>
                <p className="text-xs text-gray-400 mt-1">Profilini tamamla, teklifler gelsin</p>
              </div>
            ) : (
              jobOffers.map(offer => {
                const os = OFFER_STATUS[offer.status] || OFFER_STATUS.pending;
                return (
                  <div key={offer.id} className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-1">{offer.title}</p>
                      <Badge className={`text-[10px] border shrink-0 ${os.cls}`}>{os.label}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{offer.sender_name}</p>
                    {offer.budget && (
                      <p className="text-xs font-semibold text-emerald-600">{offer.budget} {offer.budget_currency}</p>
                    )}
                    <p className="text-xs text-gray-400 line-clamp-2 mt-1">{offer.description}</p>
                    {offer.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={() => respondToOffer(offer.id, 'accepted')}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7">
                          <CheckCircle2 className="w-3 h-3 mr-1" />Kabul Et
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => respondToOffer(offer.id, 'declined')}
                          className="flex-1 text-xs h-7 border-red-200 text-red-500 hover:bg-red-50">
                          Reddet
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-3">Hızlı İşlemler</h3>
            <div className="space-y-2">
              {[
                { label: 'Profil ve Portfolyo Düzenle', icon: Edit3, to: '/app/freelancer/profile', cls: 'bg-red-50 text-red-600 hover:bg-red-100' },
                { label: 'Public Profilimi Görüntüle', icon: Globe, href: `/#/freelancer/${profile?.slug}`, cls: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
                { label: 'İş İlanlarını Gör', icon: FileText, to: '/job-board', cls: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
              ].map(({ label, icon: Icon, to, href, cls }) => (
                <button key={label}
                  onClick={() => to ? navigate(to) : window.open(href, '_blank')}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-sm font-medium transition-colors ${cls}`}>
                  <Icon className="w-4 h-4 shrink-0" />{label}
                </button>
              ))}
            </div>
          </div>

          {/* Profile preview */}
          <div className="bg-gradient-to-br from-red-600 to-rose-700 rounded-2xl p-5 text-white shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-3">Profil Önizlemesi</p>
            <div className="flex items-center gap-3 mb-3">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="avatar" className="w-12 h-12 rounded-full object-cover border-2 border-white/30" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-white/70" />
                </div>
              )}
              <div>
                <p className="font-bold text-base">{user?.name}</p>
                <p className="text-red-200 text-xs">{stats?.category_name}</p>
              </div>
            </div>
            <p className="text-red-100 text-xs line-clamp-2 mb-3">{profile?.headline || 'Henüz başlık eklenmemiş'}</p>
            <Button size="sm" variant="outline" onClick={() => navigate('/app/freelancer/profile')}
              className="border-white/30 text-white hover:bg-white/10 text-xs gap-1">
              Düzenle <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Commission Modal */}
      {showCommission && (
        <CommissionModal profile={profile} onClose={() => setShowCommission(false)} />
      )}
    </div>
  );
}

